import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

// Curated subset of Dr Aswad's 40+ publications with real titles and journals
const PUBLICATIONS = [
  { year: 2018, journal: "Orthop Traumatol Surg Res", title: "Revision of Latarjet by Eden-Hybinette surgical technique", tags: ["Épaule", "Instabilité"] },
  { year: 2014, journal: "Semin Musculoskelet Radiol", title: "Anterior shoulder instability, the role of advanced shoulder imaging in preoperative planning", tags: ["Épaule", "Imagerie"] },
  { year: 2014, journal: "Arthroscopie (livre)", title: "Pathologie cartilagineuse du coude, pathologie synoviale du coude", tags: ["Coude", "Arthroscopie"] },
  { year: 2012, journal: "AJR Am J Roentgenol", title: "Preoperative Imaging of Anterior Shoulder Instability: MDCT Arthrography vs MR Arthrography and Arthroscopy", tags: ["Épaule", "Imagerie"] },
  { year: 2012, journal: "SFR Paris", title: "Traumatismes des MCP et IPP des doigts", tags: ["Main"] },
  { year: 2012, journal: "Sauramps (livre)", title: "Luxation sternoclaviculaire postérieure (monographie)", tags: ["Épaule", "Urgences"] },
  { year: 2009, journal: "Sauramps — Poignet et main", title: "Les croisements tendineux du poignet", tags: ["Poignet"] },
  { year: 2008, journal: "Skeletal Radiol", title: "Sonography of the painful shoulder: role of the operator's experience", tags: ["Épaule", "Imagerie"] },
  { year: 2007, journal: "Journal de Radiologie", title: "L'intervalle des rotateurs : des lésions cachées ?", tags: ["Épaule", "Imagerie"] },
  { year: 2006, journal: "Rev Chir Orthop", title: "Symposium sur l'arthroscopie du coude (technique)", tags: ["Coude", "Arthroscopie"] },
  { year: 2005, journal: "Sauramps Montpellier", title: "L'intervalle des rotateurs — étude anatomique et imagerie", tags: ["Épaule", "Anatomie"] },
  { year: 2004, journal: "SportMed N° 163", title: "Les luxations acromio-claviculaires. Pathologie médicale ou chirurgicale ?", tags: ["Épaule", "Sport"] },
  { year: 2004, journal: "Rev Chir Orthop", title: "Traitement des tendinopathies calcifiantes de la coiffe — place de l'acromioplastie", tags: ["Épaule"] },
  { year: 2004, journal: "Monographie GEM", title: "Neuropathies proximales du nerf musculo-cutané", tags: ["Nerf", "Épaule"] },
  { year: 2002, journal: "Maîtrise Orthopédique N° 113", title: "Luxation postérieure sterno-claviculaire — mise au point", tags: ["Épaule", "Urgences"] },
  { year: 2002, journal: "Rev Chir Orthop", title: "Le névrome infra-patellaire : cause méconnue de douleurs sur prothèse totale du genou", tags: ["Genou"] },
  { year: 2001, journal: "Surg Radiol Anat", title: "Anatomical basis of Latissimus dorsi and Teres major transfers in rotator cuff tear surgery", tags: ["Épaule", "Anatomie"] },
  { year: 2001, journal: "Sauramps", title: "Shoulder prostheses in rheumatoid arthritis — epidemiology and preoperative assessment", tags: ["Épaule", "Prothèse"] },
  { year: 2000, journal: "J Hand Surg", title: "Lipoma arborescens of the elbow — case report", tags: ["Coude"] },
  { year: 1998, journal: "The Cervical Spine, 3rd Ed.", title: "Extension injuries of the lower cervical spine", tags: ["Rachis"] },
  { year: 1997, journal: "Eur Spine J", title: "Indications for surgery in regular thoracic and thoracolumbar kyphosis", tags: ["Rachis"] },
  { year: 1997, journal: "Méd Chir Pied", title: "Traitement des ruptures initialement négligées du tendon calcanéen", tags: ["Pied"] },
  { year: 1996, journal: "Rev Chir Orthop", title: "Ostéosynthèse interne secondaire après fixation externe — fracture ouverte du membre inférieur", tags: ["Traumatologie"] },
  { year: 1995, journal: "J Traumatol Sport", title: "Syndrome de loge externe aigu de jambe secondaire à un exercice de marche", tags: ["Sport", "Urgences"] },
  { year: 1994, journal: "J Chir (Paris)", title: "Os styloideum et carpe bossu", tags: ["Poignet"] },
];

const PublicationsPage = () => {
  const { lang } = useLanguage();
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const tags = useMemo(() => {
    const s = new Set();
    PUBLICATIONS.forEach((p) => p.tags.forEach((t) => s.add(t)));
    return ["all", ...Array.from(s).sort()];
  }, []);

  const filtered = useMemo(() => {
    return PUBLICATIONS.filter((p) => {
      const tagOk = filter === "all" || p.tags.includes(filter);
      const q = query.trim().toLowerCase();
      const qOk = !q || p.title.toLowerCase().includes(q) || p.journal.toLowerCase().includes(q);
      return tagOk && qOk;
    }).sort((a, b) => b.year - a.year);
  }, [filter, query]);

  return (
    <main data-testid="publications-page" className="bg-white pt-28 md:pt-36 pb-20">
      <div className="container-page">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-[#FF5A1F]">
          <i className="fa-solid fa-arrow-left" aria-hidden="true"></i>
          {lang === "fr" ? "Retour à l'accueil" : "Back to home"}
        </Link>

        <div className="mt-6 max-w-3xl">
          <span className="chip mb-5">
            {lang === "fr" ? "Recherche & publications" : "Research & publications"}
          </span>
          <h1 className="font-display font-black text-5xl sm:text-6xl text-navy leading-[1.02]">
            {lang === "fr" ? "40+ publications scientifiques" : "40+ scientific publications"}
          </h1>
          <p className="mt-5 text-base md:text-lg text-foreground/75 leading-relaxed">
            {lang === "fr"
              ? "Sélection d'articles de recherche, chapitres de livres et monographies publiés par le Dr Richard Aswad dans les journaux internationaux de référence en chirurgie orthopédique et imagerie musculo-squelettique."
              : "Selected research articles, book chapters and monographs published by Dr Richard Aswad in leading international journals of orthopedic surgery and musculoskeletal imaging."}
          </p>
        </div>

        {/* Filters */}
        <div className="mt-10 rounded-2xl border border-border bg-[#F8FAFC] p-5">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                {lang === "fr" ? "Recherche" : "Search"}
              </label>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="pub-search"
                placeholder={lang === "fr" ? "Titre, journal…" : "Title, journal…"}
                className="mt-2 w-full rounded-md border border-border bg-white px-4 py-2.5 text-sm focus:border-[#FF5A1F] focus:outline-none focus:ring-2 focus:ring-[#FF5A1F]/20"
              />
            </div>
            <div className="flex-shrink-0">
              <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                {lang === "fr" ? "Filtrer" : "Filter"}
              </label>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {tags.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setFilter(t)}
                    data-testid={`pub-filter-${t}`}
                    className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.1em] border transition ${
                      filter === t
                        ? "bg-[#0A192F] text-white border-[#0A192F]"
                        : "bg-white text-navy border-border hover:border-[#FF5A1F]/40"
                    }`}
                  >
                    {t === "all" ? (lang === "fr" ? "Tout" : "All") : t}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            {filtered.length} {lang === "fr" ? "publication(s) trouvée(s)" : "publication(s) found"}
          </p>
        </div>

        {/* List */}
        <ol className="mt-8 divide-y divide-border" data-testid="publications-list">
          {filtered.map((p, i) => (
            <li key={i} className="py-5 flex items-start gap-5">
              <span className="font-display text-sm font-bold text-[#FF5A1F] w-14 shrink-0 pt-0.5">
                {p.year}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-display text-base md:text-lg font-semibold text-navy leading-snug">
                  {p.title}
                </p>
                <p className="mt-1.5 text-xs md:text-sm text-muted-foreground italic">{p.journal}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {p.tags.map((t) => (
                    <span key={t} className="text-[10px] font-semibold uppercase tracking-[0.14em] text-foreground/60 bg-muted px-2 py-0.5 rounded">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </main>
  );
};

export default PublicationsPage;
